import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Create } from '@refinedev/antd';
import { Form, Input, Button, Select, message } from 'antd';
import { authClient } from '../../providers/axios';

// Mirrors the auth-service KEY_FORMAT in sf-auth-service/src/routes/permissions.ts.
// Allows a letter-led first segment, any number of colon-separated segments, and a
// final segment that is either a normal segment or a literal ":*" wildcard.
// Valid: customer:view, admin:eva:usage:read, admin:eva:*, permissions:*
export const KEY_PATTERN = /^[a-zA-Z][a-zA-Z0-9_]*(:[a-zA-Z][a-zA-Z0-9_]*)*(:\*|:[a-zA-Z][a-zA-Z0-9_]*)$/;

export const PermissionsCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (values: {
    key: string;
    description: string;
    category: string;
    scope: string;
  }) => {
    if (submitting) return;
    setSubmitting(true);
    try {
      await authClient.post('/permissions', { ...values, isLive: false });
      message.success('Permission created');
      navigate('/permissions');
    } catch {
      message.error('Failed to create permission');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Create title="Create Permission" saveButtonProps={{ style: { display: 'none' } }}>
      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <Form.Item
          label="Key"
          name="key"
          rules={[
            { required: true, message: 'Key is required' },
            {
              pattern: KEY_PATTERN,
              message:
                'Key must match format "category:action", "category:action:subaction", or a "...:*" wildcard (letters, numbers, underscores)',
            },
          ]}
        >
          <Input
            placeholder="category:action"
            onChange={(e) => {
              const val = e.target.value;
              const [cat] = val.split(':');
              if (cat) {
                form.setFieldsValue({ category: cat });
              }
            }}
          />
        </Form.Item>

        <Form.Item
          label="Description"
          name="description"
          rules={[{ required: true, message: 'Description is required' }]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          label="Category"
          name="category"
          tooltip="Auto-derived from key prefix. Override if needed."
          rules={[{ required: true, message: 'Category is required' }]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          label="Scope"
          name="scope"
          initialValue="internal"
          rules={[{ required: true, message: 'Scope is required' }]}
        >
          <Select
            options={[
              { label: 'Internal', value: 'internal' },
              { label: 'Customer', value: 'customer' },
            ]}
          />
        </Form.Item>

        <Form.Item>
          <Button type="primary" htmlType="submit" loading={submitting}>
            Create Permission
          </Button>
        </Form.Item>
      </Form>
    </Create>
  );
};
